/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/media-has-caption */
import dayjs from "dayjs";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { classnames, Icon, Loader, Thumbnail } from "@culturehq/components";
import client, { isSignedIn } from "@culturehq/client";
import { debounce } from "lodash";

import { useProfile } from "app/context";
import { ProfileLink } from "app/links";
import { canAdminStory, canShareStory, isCulturalAmbassadorAndCreator } from "app/permissions";
import { getMediaFromStory, isMicrosoft, isSynopsys, Trans } from "app/utils";

import imagesSizes from "constants/imagesSizes";

import SelectButton from "components/admin/settings/stories/SelectButton";
import SocialShareOptions from "components/admin/settings/SocialShareOptions";
import { Time, TimeDiff } from "components/common/Time";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import TrendsetterBadge from "components/feed/feed-item/preview/story-created/TrendsetterBadge";
import CommentsList from "components/comments/CommentsList";
import CommentsWrapper from "components/comments/CommentsWrapper";
import StoryQuestionTitle from "components/stories/StoryQuestionTitle";

import StoryShareLinkedin from "components/linkedin/StoryShareLinkedin";
import SelectUserModal from "components/admin/settings/stories/SelectUserModal";
import useLatestMedia, { getLastMediaVersion } from "app/hooks/useLatestMedia";
import PostStoryVideoPreview from "components/stories/post-stories-v2/common/PostStoryForm/PostStoryVideoPreview";
import PhotoFaces from "./tags/PhotoFaces";
import PhotoTags from "./tags/PhotoTags";
import LightboxImageSlider from "./LightboxImageSlider";
import LightboxStoryQuickOptions from "./LightboxStoryQuickOptions";
import SocialChannel from "../SocialChannel";
import Subtitles from "../Subtitles";
import BodyTags from "../BodyTags";
import TextSpinner from "../TextSpinner/TextSpinner";
import SearchCareerLink from "./SearchCareerLink";

const createComment = (createdComment, comments) => {
  if (!createdComment.parentCommentId) {
    return [createdComment, ...comments];
  }

  return [
    ...comments.map(comment => {
      if (comment.id !== createdComment.parentCommentId) {
        return comment;
      }

      return {
        ...comment,
        childComments: comment.childComments.concat(createdComment)
      };
    })
  ];
};

const deleteComment = (deletedComment, comments) => {
  const filter = comment => comment.id !== deletedComment.id;

  return comments.filter(filter).map(comment => ({
    ...comment,
    childComments: comment.childComments.filter(filter)
  }));
};

const toggleCommentCheer = (commentId, cheerForUser, cheersCount) => ({ comments }) => ({
  comments: comments.map(comment => {
    if (comment.id === commentId) {
      return { ...comment, cheerForUser, cheersCount };
    }

    return {
      ...comment,
      childComments: comment.childComments.map(child => (
        child.id === commentId ? { ...child, cheerForUser, cheersCount } : child
      ))
    };
  })
});

const PreviewNewestVideoMessage = () => (
  <div className="preview-newest-video-message">
    <TextSpinner />
    <h4>This video is processing</h4>
  </div>
);

const LightboxStoryPhoto = ({
  activeStory: story,
  containerRef,
  hideLinks,
  isTrendsetter,
  language,
  tagging,
  onCommentsChange,
  onTagDelete,
  onTagStart,
  onStoryCheerToggle,
  noActions = false,
  landingPage = false,
  preview = false,
  showProfileLink = true,
  changing,
  onChangedFinished,
  onDelete,
  onStoryUpdate,
  handleApproveClicked,
  reviewVersion,
  internalVersion,
  approvedVersion,
  videoStudio,
  onAnotherModalOpen,
  organization,
  organizationId,
  setImageSliderSwipe,
  onVideoStoryPlay,
  onVideoStoryPause
}) => {
  const mediaRef = React.createRef();
  const [state, setState] = useState({
    boundingBox: null,
    commentCount: story.commentCount,
    hovering: false,
    imageLoaded: false,
    showComments: false
  });
  const { profile } = useProfile();
  const [comments, setComments] = useState([]);
  const [showMore, setShowMore] = useState(true);
  const [showVideoInfo, setShowVideoInfo] = useState(false);
  const [pausedVideo, setPausedVideo] = useState(false);
  const {
    isFetching: mediaIsFetching,
    thirdPartyMedias,
    mustProcessingMessageShow,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    media
  } = useLatestMedia(story, profile, landingPage);
  const lastMediaVersion = getLastMediaVersion(thirdPartyMedias);
  const isLastMediaVersionProcessing = lastMediaVersion?.status === "processing";
  const isLoggedIn = isSignedIn();
  const [nextApprovedForSocialMessage, setNextApprovedForSocialMessage] = useState();
  const onToggleShowMore = () => setShowMore(value => !value);

  const activeStory = useMemo(() => {
    if (landingPage) return story;

    const isMediaVersionExpired = () => {
      const mediaVersionAt = dayjs(lastMediaVersion.createdAt);
      const now = dayjs();
      return now.diff(mediaVersionAt, "day") >= 1;
    };

    const updatedStory = {
      ...story,
      media: {
        ...story.media,
        ...media,
        burnSubtitles: media.burnSubtitles ?? true
      }
    };

    if (!lastMediaVersion || lastMediaVersion.status !== "succeeded" || isMediaVersionExpired()) {
      return updatedStory;
    }

    return {
      ...updatedStory,
      media: {
        ...updatedStory.media,
        url: lastMediaVersion.url,
        shareableUrl: lastMediaVersion.url,
        subtitles: media.subtitles
      }
    };
  }, [story, lastMediaVersion, media, landingPage]);

  const debouncedCallback = useCallback(debounce(callback => callback(), 5000), []);

  useEffect(
    () => {
      setState({ ...state, imageLoaded: !changing });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changing]
  );

  useEffect(
    () => {
      let isMounted = true;

      const getNextScheduledTime = () => {
        const nextSocialPosts = activeStory.approvedForSocial?.approvedForSocialMessages?.filter(
          socialMessage => !socialMessage.shared
        )?.sort((socialMessageFirst, socialMessageSecond) => {
          const dateFrist = new Date(socialMessageFirst.scheduledAt);
          const dateSecond = new Date(socialMessageSecond.scheduledAt);
          return dateFrist - dateSecond;
        });

        if (nextSocialPosts?.length > 0) {
          setNextApprovedForSocialMessage(nextSocialPosts[0]);
        }
      };

      const getComments = async () => {
        const { comments: results } = await client.listStoryComments({ storyId: activeStory.id });
        if (isMounted) setComments(results);
      };

      if (!landingPage) {
        debouncedCallback(() => {
          getComments();
          getNextScheduledTime();
        });
      }

      return () => {
        isMounted = false;
      };
    },
    [activeStory, landingPage]
  );

  const handleCreateComment = comment => {
    if (onCommentsChange) {
      onCommentsChange(activeStory, comments.length + 1);
    }
    setComments(createComment(comment, comments));
  };

  const handleDeletedComment = comment => {
    if (onCommentsChange) {
      onCommentsChange(activeStory, comments.length - 1);
    }
    setComments(deleteComment(comment, comments));
  };

  const handleImageLoad = () => {
    setState({ ...state, imageLoaded: true });
    onChangedFinished();
  };

  const handleCommentClick = () => {
    setState({ ...state, showComments: false });
  };

  const handleCommentsChange = commentCount => {
    setState({ ...state, commentCount });
  };

  const handleCheerCommentToggle = (commentId, cheerForUser, cheersCount) => {
    setComments(comments.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, cheerForUser, cheersCount };
      }

      return {
        ...comment,
        childComments: comment.childComments.map(child => (
          child.id === commentId ? { ...child, cheerForUser, cheersCount } : child
        ))
      };
    }));
  };

  const handlePlay = () => {
    if (mediaRef.current) mediaRef.current.play();
  };

  const handlePauseVideo = (value = true) => {
    if (activeStory.media.mediaType === "video") {
      if (mediaRef.current && value) mediaRef.current.pause();
      setPausedVideo(value);
    }
  };

  const handleEditedApprovedMessage = updatedApprovedForSocialMessage => {
    const updatedStory = {
      ...activeStory,
      approvedForSocial: {
        ...activeStory.approvedForSocial,
        approvedForSocialMessages: activeStory.approvedForSocial
          .approvedForSocialMessages.map(message => {
            if (message.id === updatedApprovedForSocialMessage.id) {
              return updatedApprovedForSocialMessage;
            }

            return message;
          })
      }
    };
    onStoryUpdate(updatedStory);
  };

  const handleCreatedApprovedMessage = createdApprovedForSocial => {
    const updatedStory = { ...activeStory, approvedForSocial: createdApprovedForSocial };
    onStoryUpdate(updatedStory);
  };

  const handleVideoPlay = () => {
    if (pausedVideo) {
      handlePauseVideo();
      return;
    }

    setShowVideoInfo(false);
    if (onVideoStoryPlay) onVideoStoryPlay();
  };

  const handleVideoPause = () => {
    if (onVideoStoryPause) onVideoStoryPause();
    setShowVideoInfo(true);
  };

  const { body, creator, createdAt, question } = activeStory;
  const imageUrl = activeStory.media.mediaType === "image" ? getMediaFromStory(imagesSizes.NORMAL, activeStory) : activeStory.media.thumbnail;

  return (
    <div className="gallery-lightbox__wrapper">
      <div className="gallery-lightbox__photo col-sm-7 col-xs-12">
        {mustProcessingMessageShow && isLastMediaVersionProcessing && <PreviewNewestVideoMessage />}
        <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${imageUrl})` }}>
          {activeStory.photoTags && activeStory.photoTags.length > 0 && state.boundingBox && (
            <PhotoTags
              boundingBox={state.boundingBox}
              containerRef={containerRef}
              onTagDelete={onTagDelete}
              photo={activeStory}
              tagging={tagging}
            />
          )}
          {(activeStory.faces && activeStory.faces.length > 0 && state.boundingBox)
            && tagging
            && (
              <PhotoFaces
                boundingBox={state.boundingBox}
                containerRef={containerRef}
                onTagStart={onTagStart}
                photo={activeStory}
              />
            )}
        </div>
        {activeStory.media.mediaType === "image" && (
          <LightboxImageSlider
            boxImages={activeStory.visuals}
            onImageLoad={handleImageLoad}
            setImageSliderSwipe={setImageSliderSwipe}
          />
        )}
        {activeStory.media.mediaType === "video" && !mediaIsFetching && (
          <>
            {showVideoInfo && !isLastMediaVersionProcessing && (
              <div className="gallery-lightbox__video-play" onClick={handlePlay}><Icon icon="play-filled" /></div>
            )}

            {isLastMediaVersionProcessing
              ? (
                <PostStoryVideoPreview />
              ) : (
                <video
                  autoPlay
                  controls
                  className={classnames("gallery-lightbox__main-image gallery-lightbox__main-video", { landscape: activeStory.media.width >= activeStory.media.height }, { loading: !state.imageLoaded })}
                  onPause={handleVideoPause}
                  onPlay={handleVideoPlay}
                  onLoadedData={handleImageLoad}
                  poster={`${activeStory.media.thumbnail}?not-from-cache-please${Date.now()}`}
                  ref={mediaRef}
                  src={activeStory.media.url}
                  crossOrigin="anonymous"
                  disablePictureInPicture
                  controlsList="nodownload"
                >
                  <Subtitles language={language} media={activeStory.media} />
                </video>
              )}
          </>
        )}
        {(mediaIsFetching || (!isLastMediaVersionProcessing && !state.imageLoaded)) && (
          <Loader loading />
        )}
        <div className="Story_Badges__Container">
          {isTrendsetter && <TrendsetterBadge />}
          {activeStory?.socialShares?.map(share => share.socialType)?.filter(
            (value, index, self) => (self.indexOf(value) === index)
          )?.map(type => (
            <SocialChannel
              key={type}
              socialType={type}
              socialShare={activeStory?.socialShares.find(share => share.socialType === type)}
            />
          ))}
          {activeStory.socialShares?.length === 0
            && activeStory.approvedForSocial?.approvedForSocialMessages?.length > 0
            && canAdminStory(profile, activeStory)
            && nextApprovedForSocialMessage && (
              <StoryShareLinkedin
                approvedForSocial={activeStory.approvedForSocial}
                approvedForSocialMessage={nextApprovedForSocialMessage}
                onEdit={handleEditedApprovedMessage}
                story={activeStory}
              >
                <p className="SocialBadge linkedin">
                  <svg height="22px" viewBox="0 0 384 392" width="22px" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g transform="translate(0.000000, -0.000000)" fill="#FFFFFF">
                        <rect fillRule="nonzero" x="10" y="134" width="75" height="255.592105" rx="16" />
                        <circle fillRule="nonzero" cx="47.5" cy="47.5" r="47.5" />
                        <path d="M212.307693,169.233069 C227.965212,141.87818 257.082746,125 288.615384,125 C337.137538,125 384,153.598967 384,229.86288 L384,372.857716 C384,377.914793 381.992121,382.765181 378.414175,386.341074 C374.836229,389.916968 369.983056,391.923695 364.923076,391.923695 L326.76923,391.923695 C321.70925,391.923695 316.856078,389.916968 313.278132,386.341074 C309.700185,382.765181 307.690789,377.914793 307.692307,372.857716 L307.692307,239.395869 C307.692307,213.071246 286.339734,191.730923 260,191.730923 C233.660266,191.730923 212.307693,213.071246 212.307693,239.395869 L212.307693,372.857716 C212.309211,377.914793 210.299815,382.765181 206.721868,386.341074 C203.143922,389.916968 198.29075,391.923695 193.23077,391.923695 L155.076924,391.923695 C150.016944,391.923695 145.163771,389.916968 141.585825,386.341074 C138.007879,382.765181 136,377.914793 136,372.857716 L136,153.598967 C136,148.54189 138.007879,143.691502 141.585825,140.115609 C145.163771,136.539715 150.016944,134.531471 155.076924,134.532989 L193.23077,134.532989 C198.29075,134.531471 203.143922,136.539715 206.721868,140.115609 C210.299815,143.691502 212.309211,148.54189 212.307693,153.598967 L212.307693,169.233069 Z" />
                      </g>
                    </g>
                  </svg>
                  <span>
                    <Trans><span>Scheduled for</span></Trans>
                    {" "}
                    <Time time={nextApprovedForSocialMessage?.scheduledAt} />
                  </span>
                  <Icon className="edit-icon" icon="edit" />
                </p>
              </StoryShareLinkedin>
            )}
        </div>
      </div>

      <div className="gallery-lightbox__details-container col-sm-5 col-xs-12">
        <div className={`scrollable-container no-scrollbar ${landingPage ? "full-height" : ""}`}>
          <StoryQuestionTitle
            storyQuestion={question}
            showAccordian
            showLink={!hideLinks && !landingPage}
          />
          <hr />
          {creator && (
            <div className="gallery-lightbox__creator-container">
              <Thumbnail className="Thumbnail" image={creator.avatar.thumbUrl} title={creator.name} />
              <div className="gallery-lightbox__creator-information">
                {(hideLinks || !showProfileLink || creator.visibility === "pending") ? (
                  <span>{creator.name}</span>
                ) : (
                  <div className="user-label">
                    <ProfileLink landingPage={landingPage} preview={preview} user={creator} className="gallery-lightbox__uploader-name" />
                    {profile.organizationAdmin && (
                      <div className="chq-btn-select-user">
                        <SelectUserModal
                          user={creator}
                          onStoryUpdate={onStoryUpdate}
                          activeStory={activeStory}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="gallery-lightbox__uploader-date">
                  {landingPage ? (
                    <>
                      {creator.programs?.length > 0 ? (
                        <Trans>{creator.programs[0].name}</Trans>
                      ) : (
                        <Trans>{creator.title}</Trans>
                      )}
                    </>
                  ) : (
                    <TimeDiff time={createdAt} />
                  )}
                </div>
              </div>
              {!noActions && isLoggedIn && (
                <>
                  {canShareStory(profile, activeStory) && (
                    <SocialShareOptions
                      approvedForSocial={isCulturalAmbassadorAndCreator(profile, activeStory)
                        ? undefined : story.approvedForSocial}
                      story={activeStory}
                      onCreatedApprovedMessage={handleCreatedApprovedMessage}
                    />
                  )}
                  <LightboxStoryQuickOptions
                    isLastMediaVersionProcessing={isLastMediaVersionProcessing}
                    story={activeStory}
                    thirdPartyMediaIsFetching={mediaIsFetching}
                    thirdPartyMedias={thirdPartyMedias}
                    onDelete={onDelete}
                    onUpdate={onStoryUpdate}
                    onAnotherModalOpen={onAnotherModalOpen}
                    onPauseVideo={handlePauseVideo}
                    onThirdPartyMediasReload={onThirdPartyMediasReload}
                    onUpdateDraftVersion={onUpdateDraftVersion}
                    handleApproveClicked={handleApproveClicked}
                  />
                </>
              )}
            </div>
          )}

          <LazyEditorOutput output={body} landingPage={landingPage} showTags={false} />
          <BodyTags hideLinks landingPage={landingPage} tags={activeStory.bodyTags} />

          {!noActions && isLoggedIn && (
            <CommentsList
              createCall={client.createStoryComment}
              comments={comments}
              hideLinks={hideLinks}
              showAvatar={false}
              onCreateComment={handleCreateComment}
              onChange={handleCommentsChange}
              onCheerToggle={handleCheerCommentToggle}
              onDeleteComment={handleDeletedComment}
              options={{ storyId: activeStory.id }}
              profile={profile}
              showMore={showMore}
              onToggleShowMore={onToggleShowMore}
            />
          )}

          {landingPage && (
            <div className="bottom-shadow">
              {isMicrosoft(organization) && (
                <SearchCareerLink
                  buttonClass="MicrosoftButton"
                  organizationId={organizationId}
                  story={activeStory}
                  url="https://careers.microsoft.com/v2/global/en/home.html"
                />
              )}
              {isSynopsys(organization) && (
                <SearchCareerLink
                  buttonClass="SynopsysButton"
                  organizationId={organizationId}
                  story={activeStory}
                  url="https://careers.synopsys.com/search-jobs"
                />
              )}
            </div>
          )}
        </div>

        {!noActions && isLoggedIn && (
          <CommentsWrapper
            commentCount={activeStory.commentCount}
            createCall={client.createStoryComment}
            onCommentClick={handleCommentClick}
            onChange={handleCommentsChange}
            onCreateComment={handleCreateComment}
            onCheerToggle={onStoryCheerToggle}
            onToggleCommentCheer={toggleCommentCheer}
            options={{ storyId: activeStory.id }}
            profile={profile}
            showAvatar={false}
            story={activeStory}
            showButton={false}
          >
            <>
              {(reviewVersion || internalVersion || approvedVersion) && (
                <div className="gallery-lightbox__share">
                  <Icon icon="info" />
                  <Trans>Cheers/comments are internal only</Trans>
                </div>
              )}
            </>
          </CommentsWrapper>
        )}

        {videoStudio && (
          <div className="row">
            <div className="col-xs-12">
              <hr />
              <div className="gallery-lightbox__actions">
                <SelectButton story={activeStory} onClick={handleApproveClicked} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LightboxStoryPhoto;
